import React from "react";
import { Chip } from "../../../ForBrands/ui/BrandUI";
import searchIcon from "../../../../assets/brand/onboarding/search.svg";
import checkWhite from "../../../../assets/brand/onboarding/check-12x9.svg";
import rowChevron from "./icons/team-row-chevron.svg";

// Pieces of the coach onboarding frames (05 to 09), sized from Figma. The
// frame, title block, fields, notes and buttons come from auth/v2/pieces.

// Chips for one pick (Sport, Tier, Region). Options not live yet show a
// SOON tag and cannot be picked. `clearable` lets a second click unpick.
export const ChoiceChips = ({ label, options, value, onChange, clearable = false }) => (
  <div role="group" aria-label={label} className="flex flex-wrap gap-2">
    {options.map((o) => {
      const soon = !!o.status && o.status !== "live";
      const active = value === o.value;
      return (
        <Chip
          key={o.value}
          active={active}
          aria-pressed={active}
          disabled={soon}
          count={soon ? "SOON" : undefined}
          onClick={() => onChange(clearable && active ? null : o.value)}
          className={soon ? "cursor-not-allowed opacity-50 hover:!bg-white" : ""}
        >
          {o.name}
        </Chip>
      );
    })}
  </div>
);

// The 56px search field (Figma "Input · Mobile" with a 16px search icon).
export const SearchField = ({ inputRef, className = "", children, ...props }) => (
  <div className={`flex h-14 items-center gap-[10px] rounded-[12px] border border-bm-border bg-white px-[18px] focus-within:border-bm-ink focus-within:shadow-[inset_0_0_0_1px_#2c2c2c] ${className}`}>
    <img src={searchIcon} alt="" className="h-4 w-4 shrink-0" />
    <input ref={inputRef} className="ob-bare min-w-0 flex-1" {...props} />
    {children}
  </div>
);

// Figma "TeamRow": a 40px dark crest with initials, the name and a meta
// line; a chevron, or the orange check once picked.
export const TeamPickRow = ({ initials, name, meta, selected = false, disabled = false, onClick }) => (
  <button
    type="button"
    aria-pressed={selected}
    disabled={disabled}
    onClick={onClick}
    className={`flex w-full items-center justify-between gap-4 rounded-[12px] text-left disabled:cursor-not-allowed disabled:opacity-50 ${
      selected ? "border-2 border-bm-orange bg-bm-orange/[0.08] px-[15px] py-[13px]" : "border border-bm-border bg-white px-4 py-[14px] hover:border-bm-strong"
    }`}
  >
    <span className="flex min-w-0 items-center gap-[14px]">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-bm-ink bm-label-l text-white">{initials}</span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate bm-h-s text-bm-ink">{name}</span>
        {meta && <span className="truncate bm-body-m text-bm-muted">{meta}</span>}
      </span>
    </span>
    {selected ? (
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bm-orange">
        <img src={checkWhite} alt="" className="h-[9px] w-3" />
      </span>
    ) : (
      <img src={rowChevron} alt="" className="h-[15px] w-[9px] shrink-0" />
    )}
  </button>
);

// The dashed block shown when a scope lists no team.
export const EmptyBlock = ({ title, children, action }) => (
  <div className="flex flex-col items-center gap-[14px] rounded-[24px] border-[1.5px] border-dashed border-bm-strong bg-bm-subtle px-6 py-9 text-center">
    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bm-mutedbg">
      <img src={searchIcon} alt="" className="h-5 w-5 opacity-60" />
    </span>
    <span className="bm-label-l text-bm-ink">{title}</span>
    <span className="bm-body-s text-bm-muted">{children}</span>
    {action}
  </div>
);

// Figma 09 hero band: a 72px outline avatar (16px radius), a badge, the
// coach's name and their team line, on a 560px row. `phone` is the mobile
// hero (2466:19261): a row at least 154px tall with the text centred, and
// the name and team line wrap anywhere so a long name (or the email used in
// its place) never runs past a 320px screen.
export const VerificationHero = ({ initials, badge, name, sub, phone = false }) => (
  <div className={phone ? "flex min-h-[154px] items-center gap-4 py-4" : "flex items-center gap-4 py-10"}>
    <span className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-[16px] border border-bm-orange bg-white bm-h-l text-bm-orange">{initials}</span>
    <div className="flex min-w-0 flex-col items-start gap-[5px]">
      {badge}
      <h1 className={phone ? "bm-h-l text-bm-ink [overflow-wrap:anywhere]" : "bm-h-l text-bm-ink"}>{name}</h1>
      {sub && <p className={phone ? "bm-body-m text-bm-ink [overflow-wrap:anywhere]" : "bm-body-m text-bm-ink"}>{sub}</p>}
    </div>
  </div>
);

// Figma "KeyValue" card on 09: 16px radius, 18px side padding.
export const SettingsCard = ({ children }) => (
  <div className="flex flex-col divide-y divide-bm-mutedbg rounded-[16px] border border-bm-border bg-white px-[18px] py-1">{children}</div>
);
